import type { EintragVerortung, PlanCoordinates, ProjectPlan, ProjektVerortung } from '../../types';
import type { Ebene, EbenenSchaetzung } from './ebenen';
import { eintragVerortung, type Ortsvorschlag, type SensorSchnappschuss } from './ortsvorschlag';
import { planFuerEbene } from './projektVerortung';
import { livePunktFuerPlan } from './liveStandort';

/**
 * Automatischer Ort im Mangel-Wizard (Betreiber 27.09.: „keine automatische
 * Verortung, man muss den Ort eintippen“). Gefüllt wird NUR ein leeres
 * Ortsfeld — Eingetipptes wird nie überschrieben; der Wizard zeigt „automatisch“
 * mit „Rückgängig“.
 *
 * Schwellen: Geschoss ab Sicherheit 0,5 (sonst lieber die Geschoss-Knöpfe
 * anbieten), Pin nur bei GPS ≤ 8 m (wie der Foto-Vorschlag).
 */

export const MIN_AUTO_SICHERHEIT = 0.5;
export const AUTO_MAX_GPS_M = 8;

export interface AutoOrt {
    ebene: Ebene | null;
    planId: string | null;
    pin: PlanCoordinates | null;
    eintrag: EintragVerortung;
    quellen: ('Luftdruck' | 'GPS' | 'Kompass')[];
}

const sichereEbene = (s: EbenenSchaetzung | null | undefined): Ebene | null =>
    s && !s.zwischen && s.ebene && s.sicherheit >= MIN_AUTO_SICHERHEIT ? s.ebene : null;

/** Aus den Live-Sensoren (ohne Foto): Geschoss + ggf. Pin auf dem Plan dieses Geschosses bzw. einem Lageplan. */
export function autoOrtAusLive(e: {
    verortung: ProjektVerortung;
    plaene: readonly ProjectPlan[];
    schaetzung: EbenenSchaetzung | null;
    schnappschuss: SensorSchnappschuss | null;
}): AutoOrt | null {
    const { verortung, plaene, schaetzung, schnappschuss } = e;
    if (!schnappschuss) return null;
    const ebene = sichereEbene(schaetzung);
    // Plan: der des Geschosses; ohne Geschoss ein georeferenzierter Lageplan (draußen).
    const kandidaten: ProjectPlan[] = [];
    const geschossPlan = ebene ? planFuerEbene(verortung, plaene, ebene) : null;
    if (geschossPlan) kandidaten.push(geschossPlan);
    if (!ebene || ebene.art === 'geruest') {
        for (const p of plaene) if (verortung.plaene?.[p.id]?.art === 'lageplan' && !kandidaten.includes(p)) kandidaten.push(p);
    }
    let planId: string | null = null;
    let punkt: ReturnType<typeof livePunktFuerPlan> = null;
    for (const p of kandidaten) {
        const lp = livePunktFuerPlan(verortung.plaene?.[p.id], schnappschuss, AUTO_MAX_GPS_M);
        if (lp) { planId = p.id; punkt = lp; break; }
    }
    if (!ebene && !punkt) return null;
    const quellen: AutoOrt['quellen'] = [];
    if (ebene) quellen.push('Luftdruck');
    if (punkt) quellen.push('GPS');
    if (punkt?.blickGradPlan !== null && punkt?.blickGradPlan !== undefined) quellen.push('Kompass');
    return {
        ebene,
        planId,
        pin: punkt?.pin ?? null,
        eintrag: {
            quelle: 'sensor',
            ebene: ebene?.name ?? null,
            hoeheM: schaetzung && ebene ? schaetzung.hoeheM : null,
            genauigkeitM: punkt?.genauigkeitM ?? null,
            blickGradPlan: punkt?.blickGradPlan ?? null,
            sicherheit: schaetzung && ebene ? schaetzung.sicherheit : null,
        },
        quellen,
    };
}

/** Aus dem Foto-Vorschlag der eingebauten Kamera. null, wenn er zu unsicher ist (dann Karte „Übernehmen“). */
export function autoOrtAusVorschlag(v: Ortsvorschlag | null | undefined): AutoOrt | null {
    if (!v) return null;
    const ebene = sichereEbene(v.schaetzung);
    const pin = v.planId && v.pin ? v.pin : null;
    if (!ebene && !pin) return null;
    const basis = eintragVerortung(v);
    return {
        ebene,
        planId: pin ? v.planId : null,
        pin,
        // Ebene nur, wenn sicher genug (sonst stünde am Eintrag ein Geschoss, das der Ortstext nicht nennt);
        // ohne Pin kein planbezogener Blick, keine Genauigkeit (Codex-Review 27.09.).
        eintrag: { ...basis, ebene: ebene?.name ?? null, ...(pin ? {} : { blickGradPlan: null, genauigkeitM: null }) },
        quellen: v.quellen.filter((q) => (q === 'Luftdruck' ? !!ebene : q === 'GPS' ? !!pin : !!pin && basis.blickGradPlan !== null)),
    };
}
